export interface ShareContent {
  title: string;
  text?: string;
  /** A public, canonical path or absolute URL. Never pass returnTo/auth params. */
  url: string;
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled';

/** `text` to the clipboard; rejects where the clipboard API is unavailable (an in-app webview, plain http). */
export function copyText(text: string): Promise<void> {
  if (!navigator.clipboard?.writeText) return Promise.reject(new Error('clipboard_unavailable'));
  return navigator.clipboard.writeText(text);
}

/** Turns a public app path into the absolute link expected by native share sheets. */
export function absoluteShareUrl(url: string, origin = window.location.origin): string {
  return new URL(url, origin).toString();
}

/**
 * Opens the platform share sheet on iOS/Android (and supporting desktop
 * browsers). Web browsers without Web Share get a clipboard fallback.
 */
export async function share(content: ShareContent): Promise<ShareOutcome> {
  const payload: ShareData = {
    title: content.title.trim(),
    text: content.text?.trim() || undefined,
    url: absoluteShareUrl(content.url),
  };

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (error) {
      // Closing the native sheet is not an error and must not copy behind the
      // person's back. Actual native failures can still fall back to copy.
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    }
  }

  await copyText(payload.url || '').catch(() => { throw new Error('Sharing is unavailable'); });
  return 'copied';
}
